import { type FC, useMemo, useState } from "react";
import { type ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { CrudLink, CustomCellDate } from "@/shared/components";
import { touchSummary } from "@/shared/components/AttributionTouch";
import {
  AssistantDialogBlock,
  OriginalEmailDialog,
  RequestSummaryCard,
  ThreadComposer,
  ThreadFeed,
  type ComposerSubmit,
} from "@/shared/components/SupportThread";
import { PagePaths } from "@/admin/shared/constants";
import {
  ReplySupportRequestMutation,
  SetSupportRequestStatusMutation,
  SupportRequestMessageOriginalQuery,
  type SupportRequestShowData,
} from "@/admin/entities/assistant";
import {
  SupportRequestStatusType,
  type ReplySupportRequestMutationType,
  type ReplySupportRequestMutationVariablesType,
  type SetSupportRequestStatusMutationType,
  type SetSupportRequestStatusMutationVariablesType,
  type SupportRequestMessageOriginalQueryType,
  type SupportRequestMessageOriginalQueryVariablesType,
} from "@/types/graphql/admin.generated";
import { typeLabel } from "../model/labels";
import { renderSupportRequestStatus } from "../model/config";
import {
  REQUEST_REFETCH_QUERIES,
  requestErrorText,
  toAdminDialogMessages,
  toAdminThreadMessages,
} from "../model/thread";

interface SupportRequestDetailsProps {
  /** CrudShow разворачивает данные showQuery в пропсы компонента. */
  supportRequest?: SupportRequestShowData | null;
  /** id обращения CrudShow передаёт отдельным пропом; здесь не нужен. */
  id?: string;
}

/** Строка сведений справа: иконка, подпись мелко, значение. */
const InfoItem: FC<{
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}> = ({ icon, label, children }) => (
  <ListItem sx={{ alignItems: "flex-start", py: 1 }}>
    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>{icon}</ListItemIcon>
    <ListItemText
      primary={label}
      secondary={children}
      slotProps={{
        primary: { variant: "caption", color: "text.secondary" },
        secondary: {
          component: "div",
          variant: "body2",
          color: "text.primary",
          sx: { mt: 0.25, minWidth: 0, wordBreak: "break-word" },
        },
      }}
    />
  </ListItem>
);

/** Ширина колонки переписки, как в карточке партнёра. */
const COLUMN_WIDTH = 920;

const REFETCH = [...REQUEST_REFETCH_QUERIES];
const formatTime = (iso: string) => new Date(iso).toLocaleString("ru-RU");

/**
 * Карточка обращения в админке: сведения, диалог с помощником, лента и
 * поле ответа из общих компонентов SupportThread (тексты русские,
 * defaultValue tShared). Админка целиком русская, без i18n-форматтеров.
 * Опрос карточки раз в 30 секунд задаёт шаг CRUD (SupportRequests.tsx).
 */
export const SupportRequestDetails: FC<SupportRequestDetailsProps> = ({
  supportRequest,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [originalId, setOriginalId] = useState<string | null>(null);
  const onError = (error: ApolloError) =>
    enqueueSnackbar(requestErrorText(error), { variant: "error" });
  const [reply, { loading: replying }] = useMutation<
    ReplySupportRequestMutationType,
    ReplySupportRequestMutationVariablesType
  >(ReplySupportRequestMutation, { refetchQueries: REFETCH, onError });
  const [setStatus, { loading: settingStatus }] = useMutation<
    SetSupportRequestStatusMutationType,
    SetSupportRequestStatusMutationVariablesType
  >(SetSupportRequestStatusMutation, { refetchQueries: REFETCH, onError });
  const [loadOriginal, original] = useLazyQuery<
    SupportRequestMessageOriginalQueryType,
    SupportRequestMessageOriginalQueryVariablesType
  >(SupportRequestMessageOriginalQuery, { fetchPolicy: "network-only" });

  const messages = useMemo(
    () => toAdminThreadMessages(supportRequest?.messages ?? []),
    [supportRequest?.messages]
  );
  const dialog = useMemo(
    () => toAdminDialogMessages(supportRequest?.assistantMessages ?? []),
    [supportRequest?.assistantMessages]
  );

  if (!supportRequest) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          Обращение не найдено
        </Typography>
      </Paper>
    );
  }

  const resolved =
    supportRequest.status === SupportRequestStatusType.ClosedType;
  const paymentLabel = supportRequest.payment
    ? `${supportRequest.payment.amount.value} ${supportRequest.payment.amount.currency}`
    : null;

  const handleStatus = () => {
    void setStatus({
      variables: {
        id: supportRequest.id,
        input: {
          status: resolved
            ? SupportRequestStatusType.OpenType
            : SupportRequestStatusType.ClosedType,
        },
      },
      onCompleted: () =>
        enqueueSnackbar(
          resolved ? "Обращение открыто снова" : "Обращение решено",
          { variant: "success" }
        ),
    });
  };

  const handleSubmit = async ({ text, resolve, internal }: ComposerSubmit) => {
    const result = await reply({
      variables: { id: supportRequest.id, input: { text, resolve, internal } },
    });
    if (result.errors?.length || !result.data) return false;
    enqueueSnackbar(
      internal
        ? "Заметка сохранена"
        : resolve
          ? "Обращение решено"
          : "Ответ отправлен",
      { variant: "success" }
    );
    return true;
  };

  const title = `#${supportRequest.number ?? ""} ${supportRequest.subject}`;
  const viewerName =
    supportRequest.user?.displayName || supportRequest.user?.email || null;
  const typeText = typeLabel[supportRequest.type] ?? supportRequest.type;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
        >
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={title} placement="bottom-start">
              <Typography variant="h6" component="h2" noWrap>
                {title}
              </Typography>
            </Tooltip>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {renderSupportRequestStatus(supportRequest.status)}
              <Chip size="small" variant="outlined" label={typeText} />
              {supportRequest.platform && (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<LiveTvOutlinedIcon />}
                  label={supportRequest.platform.title}
                />
              )}
            </Stack>
          </Stack>
          <Button
            variant={resolved ? "outlined" : "contained"}
            disableElevation
            color={resolved ? "inherit" : "success"}
            startIcon={resolved ? <ReplayIcon /> : <CheckRoundedIcon />}
            disabled={settingStatus}
            onClick={handleStatus}
            sx={{ flexShrink: 0 }}
          >
            {resolved ? "Открыть снова" : "Решить"}
          </Button>
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: "column-reverse", md: "row" }}
        spacing={2}
        alignItems="flex-start"
      >
        <Stack
          spacing={2}
          sx={{
            flex: 1,
            minWidth: 0,
            width: 1,
            maxWidth: { md: COLUMN_WIDTH },
          }}
        >
          <RequestSummaryCard
            viewerName={viewerName}
            summary={{
              typeLabel: typeText,
              message: supportRequest.message,
              details: supportRequest.details ?? null,
              paymentLabel,
              paymentHref: null,
            }}
          />
          <AssistantDialogBlock
            messages={dialog}
            formatTime={formatTime}
            viewerName={viewerName}
            defaultExpanded={!messages.some((m) => m.author !== "SYSTEM")}
          />
          <ThreadFeed
            messages={messages}
            formatTime={formatTime}
            viewerName={viewerName}
            onShowOriginal={(messageId) => {
              setOriginalId(messageId);
              void loadOriginal({ variables: { id: messageId } });
            }}
          />
          {/* Поле ответа прилипает к низу окна, пока листается лента. */}
          <Box sx={{ position: "sticky", bottom: 16, zIndex: 1 }}>
            <ThreadComposer
              placeholderHint={null}
              sending={replying}
              canResolve={!resolved}
              onSubmit={handleSubmit}
            />
          </Box>
        </Stack>

        <Card sx={{ width: { xs: 1, md: 340 }, flexShrink: 0 }}>
          <CardHeader
            title="Сведения"
            slotProps={{ title: { variant: "subtitle1", fontWeight: 600 } }}
            sx={{ px: 2, pt: 2, pb: 1 }}
          />
          <CardContent sx={{ p: 0, "&:last-child": { pb: 1 } }}>
            <List dense disablePadding>
              <InfoItem
                icon={<BusinessOutlinedIcon fontSize="small" />}
                label="Организация"
              >
                {supportRequest.organization ? (
                  <CrudLink
                    path={PagePaths.organizations}
                    tab="details"
                    id={supportRequest.organization.id}
                  >
                    {supportRequest.organization.title}
                  </CrudLink>
                ) : (
                  "-"
                )}
              </InfoItem>
              <InfoItem
                icon={<LiveTvOutlinedIcon fontSize="small" />}
                label="Арена"
              >
                {supportRequest.platform ? (
                  <CrudLink
                    path={PagePaths.platforms}
                    tab="details"
                    id={supportRequest.platform.id}
                  >
                    {supportRequest.platform.title}
                  </CrudLink>
                ) : (
                  "-"
                )}
              </InfoItem>
              <InfoItem
                icon={<PersonOutlineIcon fontSize="small" />}
                label="Зритель"
              >
                {supportRequest.user ? (
                  <CrudLink
                    path={PagePaths.users}
                    tab="details"
                    id={supportRequest.user.id}
                  >
                    {supportRequest.user.displayName ||
                      supportRequest.user.email ||
                      supportRequest.user.id}
                  </CrudLink>
                ) : (
                  "-"
                )}
              </InfoItem>
              <InfoItem
                icon={<AssignmentIndOutlinedIcon fontSize="small" />}
                label="Ответственный"
              >
                {supportRequest.assignee?.displayName ||
                  supportRequest.assignee?.email ||
                  "Не назначен"}
              </InfoItem>
              <InfoItem
                icon={<SmartToyOutlinedIcon fontSize="small" />}
                label="Диалог"
              >
                {supportRequest.conversationId ? (
                  <CrudLink
                    path={PagePaths.assistantConversations}
                    tab="details"
                    id={supportRequest.conversationId}
                  >
                    Открыть диалог
                  </CrudLink>
                ) : (
                  "-"
                )}
              </InfoItem>
              {supportRequest.payment && (
                <InfoItem
                  icon={<CreditCardOutlinedIcon fontSize="small" />}
                  label="Платёж"
                >
                  <CrudLink
                    path={PagePaths.payments}
                    tab="details"
                    id={supportRequest.payment.id}
                  >
                    {paymentLabel}
                  </CrudLink>
                </InfoItem>
              )}
              <InfoItem
                icon={<CalendarTodayOutlinedIcon fontSize="small" />}
                label="Создано"
              >
                <CustomCellDate date={supportRequest.createdAt as string} />
              </InfoItem>
              <InfoItem
                icon={<CampaignOutlinedIcon fontSize="small" />}
                label="Пришёл"
              >
                {touchSummary(supportRequest.utm)}
              </InfoItem>
              {supportRequest.closedAt && (
                <InfoItem
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  label="Решено"
                >
                  <CustomCellDate date={supportRequest.closedAt as string} />
                </InfoItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Stack>

      <OriginalEmailDialog
        open={Boolean(originalId)}
        html={original.data?.supportRequestMessageOriginal ?? null}
        loading={original.loading}
        onClose={() => setOriginalId(null)}
      />
    </Stack>
  );
};
